'use client';

import {
  Box,
  Button,
  Card,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  ListItemText,
  MenuItem,
  Select,
  TextField,
  Typography,
  InputAdornment,
  Chip,
  Alert,
  Snackbar,
  FormHelperText,
} from '@mui/material';
import { useEffect, useState } from 'react';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import NotificationAddIcon from '@mui/icons-material/NotificationAdd';
import { useParams, usePathname, useRouter } from 'next/navigation';
import CloseIcon from '@mui/icons-material/Close';
import { currencyFormatter } from '@web/utils/currency';
import { FlexBox } from '@web/components/flex-box';
import fetcher from '@web/utils/fetch';
import { differenceInDays } from 'date-fns';
import useSWRImmutable from 'swr/immutable';

const Notification = ({ product, isMobile, isNotified, user }: any) => {
  const router = useRouter();
  const pathname = usePathname();
  const params = useParams();
  const [filteredOffers, setFilteredOffers] = useState<any>([]);
  const [notified, setNotified] = useState(isNotified);
  const [open, setOpen] = useState(false);
  const [sellers, setSellers] = useState<any>([]);
  const [targetPrice, setTargetPrice] = useState('');
  const [success, setSuccess] = useState(false);

  const { data: productWithOffers } = useSWRImmutable(
    params?.slug && open && `/api/products/${params?.slug}`,
    fetcher
  );

  useEffect(() => {
    if (!productWithOffers) return;
    const diffDays = differenceInDays(
      new Date(),
      new Date(product.last_price_updated_at)
    );
    const filtered = productWithOffers.offers
      ?.filter((offer) => !offer.seller_name.toLowerCase().includes('amazon'))
      .filter((offer) => {
        if (productWithOffers.offers.length !== 1 && offer.seller_id === 156)
          return false;
        if (diffDays < 8) {
          if (
            differenceInDays(
              new Date(product.last_price_updated_at),
              new Date(offer.created_at)
            ) > 7
          ) {
            return false;
          }
        }
        return true;
      });

    setFilteredOffers(filtered);
  }, [productWithOffers]);

  const handleSellerChange = (event) => {
    const { value } = event.target;
    const selectAllSelected = value.includes('all');
    if (selectAllSelected) {
      if (value.length === 1) {
        setSellers(product?.offers.map((offer) => offer.seller_id));
      } else if (value.length === 2) {
        const newSellers = sellers.filter(
          (seller) => seller !== value[value.length - 1]
        );
        setSellers(newSellers);
      } else {
        setSellers([]);
      }
    } else {
      setSellers(value);
    }
  };
  const allSelected = sellers?.length === filteredOffers?.length;
  const handleButtonClick = async () => {
    if (!user) {
      await router.push(`/api/auth/login?returnTo=${pathname}`);
      return;
    }

    if (notified) {
      return;
    }
    setOpen(true);
  };

  const handleSubmit = async () => {
    setOpen(false);
    const body = {
      user_id: user?.sub,
      pid: product.id,
      email: user?.email,
      date_when_subscribed: product.last_price_updated_at,
      price_when_subscribed: productWithOffers?.offers[0].base_price,
      sellers,
      target_price: Math.round(Number(targetPrice) * 100) / 100,
    };

    const response = await fetch('/api/notifications', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });

    if (response.ok) {
      setSuccess(true);
      setNotified(true);
      setTimeout(() => setSuccess(false), 6000);
    }
  };

  return (
    <>
      {isMobile ? (
        <Card sx={{ my: 2 }}>
          {(notified !== undefined || !user) && (
            <Button
              variant={notified ? 'text' : 'contained'}
              color="primary"
              fullWidth
              endIcon={
                notified ? <NotificationsActiveIcon /> : <NotificationAddIcon />
              }
              onClick={handleButtonClick}
            >
              {notified
                ? 'You have already selected to be notified'
                : 'Notify me when price drops'}
            </Button>
          )}
        </Card>
      ) : (
        <Box>
          <Button
            variant={notified ? 'text' : 'contained'}
            color="primary"
            endIcon={
              notified ? <NotificationsActiveIcon /> : <NotificationAddIcon />
            }
            onClick={handleButtonClick}
          >
            {notified ? 'Notified' : ' Notify me when price drops'}
          </Button>
        </Box>
      )}
      <Dialog
        maxWidth="sm"
        fullWidth
        open={open}
        onClose={() => setOpen(false)}
        PaperProps={{
          style: {
            marginTop: 100,
            marginBottom: 'auto',
            position: 'absolute',
            top: '0',
          },
        }}
      >
        <DialogTitle>
          <FlexBox alignItems="center">
            <Box>Set Price Alert</Box>
            <NotificationAddIcon color="primary" />
          </FlexBox>

          <IconButton
            aria-label="close"
            onClick={() => setOpen(false)}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <FormControl
            fullWidth
            variant="outlined"
            margin="normal"
            size="small"
          >
            <InputLabel>Select Sellers</InputLabel>
            <Select
              multiple
              value={allSelected ? ['all'] : sellers}
              onChange={handleSellerChange}
              label="Sellers"
              renderValue={(selected) => {
                if (allSelected) {
                  return 'All sellers selected';
                }
                const displayedSellers = selected.slice(0, 5);
                const moreCount = selected.length - 5;

                return (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {displayedSellers.map((value) => {
                      const sellerName = filteredOffers.find(
                        (offer) => offer.seller_id === value
                      )?.seller_name;
                      return <Chip key={value} label={sellerName} />;
                    })}
                    {moreCount > 0 && (
                      <Box
                        sx={{ display: 'flex', alignItems: 'center' }}
                      >{`... ${moreCount} more`}</Box>
                    )}
                  </Box>
                );
              }}
              MenuProps={{
                PaperProps: {
                  style: {
                    maxHeight: '500px',
                    overflow: 'auto',
                  },
                },
              }}
            >
              <MenuItem value="all">
                <Checkbox checked={allSelected} />
                <ListItemText primary="Select All" />
              </MenuItem>
              {filteredOffers?.map((offer) => (
                <MenuItem key={offer.seller_id} value={offer.seller_id}>
                  <Checkbox checked={sellers?.includes(offer.seller_id)} />
                  <ListItemText primary={offer.seller_name} />
                  <Typography variant="body2" color="text.secondary">
                    {currencyFormatter.format(offer.base_price)}
                  </Typography>
                </MenuItem>
              ))}
            </Select>
            <FormHelperText>
              Price notification is not available for amazon
            </FormHelperText>
          </FormControl>
          <TextField
            fullWidth
            size="medium"
            variant="outlined"
            margin="normal"
            label="Target Price"
            type="number"
            value={targetPrice}
            onChange={(e) => setTargetPrice(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">$</InputAdornment>
              ),
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSubmit}
            disabled={sellers.length === 0 || !targetPrice}
          >
            Submit
          </Button>
        </DialogActions>
      </Dialog>
      <Snackbar
        open={success}
        autoHideDuration={3000}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert severity="success" sx={{ width: '100%' }}>
          Success! 🎉 You&apos;ve set up a price alert. We&apos;ll notify you
          once the product reaches your target price.
        </Alert>
      </Snackbar>
    </>
  );
};
export default Notification;
